import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import * as React from "react";
import { useMemo, useState } from "react";
import { CalendarDays, Sparkles, Loader2, Lock, Plus, Trash2, Pencil } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { ClassevivaImport } from "@/components/ClassevivaImport";
import { ClassroomImport } from "@/components/ClassroomImport";
import { PlanTasksSection } from "@/components/PlanTasksSection";
import { toast } from "sonner";
import type { DayButton } from "react-day-picker";
import { cn } from "@/lib/utils";
import { ClassScheduleSection, HomeworkSection } from "@/components/ClassScheduleSection";
import { useTranslation } from "react-i18next";
import { dateLocaleFor } from "@/lib/date-locale";
import { ErrorState, LoadingState } from "@/components/states";
import type { TFunction } from "i18next";

export const Route = createFileRoute("/_app/planner")({ component: PlannerPage });

type CalEvent = {
  id: string;
  title: string;
  notes: string | null;
  event_type: string;
  source: string;
  subject_name: string | null;
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  is_done: boolean;
};

/**
 * Tipi di evento con colore associato al cerchio nel calendario.
 * I default richiesti: verifica = giallo canarino, compito = grigio,
 * orale = arancione, hobby = rosso.
 */
function getEventTypes(t: TFunction): Record<string, { label: string; color: string; tone: string }> {
  return {
    verifica: { label: t("planner.eventTypes.verifica"), color: "#F5D547", tone: "peach" },
    compito: { label: t("planner.eventTypes.compito"), color: "#9CA3AF", tone: "sage" },
    orale: { label: t("planner.eventTypes.orale"), color: "#F97316", tone: "peach" },
    hobby: { label: t("planner.eventTypes.hobby"), color: "#EF4444", tone: "peach" },
    event: { label: t("planner.eventTypes.event"), color: "#3B82F6", tone: "sky" },
    task: { label: t("planner.eventTypes.task"), color: "#9CA3AF", tone: "sage" },
  };
}

const COLOR_FOR_TYPE: Record<string, string> = {
  verifica: "#F5D547",
  compito: "#9CA3AF",
  orale: "#F97316",
  hobby: "#EF4444",
  event: "#3B82F6",
  task: "#9CA3AF",
};

function colorFor(type: string) {
  return COLOR_FOR_TYPE[type] ?? "#9CA3AF";
}

/** Testo scuro sui colori chiari (giallo), bianco sugli altri. */
function textOn(color: string) {
  return color.toLowerCase() === "#f5d547" ? "#111" : "#fff";
}

function dayKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function PlannerPage() {
  const { t, i18n } = useTranslation();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const qc = useQueryClient();
  const EVENT_TYPES = useMemo(() => getEventTypes(t), [t]);

  const eventsQ = useQuery({
    queryKey: ["calendar_events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("calendar_events")
        .select("id,title,notes,event_type,source,subject_name,starts_at,ends_at,all_day,is_done")
        .order("starts_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CalEvent[];
    },
  });

  const events = eventsQ.data ?? [];
  const selected = date ?? new Date();
  const dayEvents = useMemo(
    () => events.filter((e) => isSameDay(new Date(e.starts_at), selected)),
    [events, selected],
  );

  /** Mappa giorno → eventi (ordinati per orario) per il rendering colorato. */
  const eventsByDay = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    for (const e of events) {
      const d = new Date(e.starts_at);
      const k = dayKey(d);
      const arr = m.get(k);
      if (arr) arr.push(e);
      else m.set(k, [e]);
    }
    return m;
  }, [events]);

  const ColoredDayButton = useMemo(() => makeColoredDayButton(eventsByDay), [eventsByDay]);

  return (
    <div className="space-y-5 px-5 pt-8">
      <PageHeader
        tone="peach"
        icon={<CalendarDays className="h-5 w-5" />}
        title={t("planner.title")}
        subtitle={t("planner.subtitle")}
        action={
          <>
            <AddEventDialog
              defaultDate={selected}
              onDone={() => qc.invalidateQueries({ queryKey: ["calendar_events"] })}
            />
            {/* HIDDEN FOR V1 - APPLE 5.2.2 - No third-party credentials scraping */}
            {false && (
              <ClassevivaImport onDone={() => qc.invalidateQueries({ queryKey: ["calendar_events"] })} />
            )}
            {/* HIDDEN FOR V1 - APPLE 5.2.2 - No third-party credentials scraping */}
            {false && (
              <ClassevivaImport
                provider="axios"
                onDone={() => qc.invalidateQueries({ queryKey: ["calendar_events"] })}
              />
            )}
            <span className="w-full text-[11px] text-muted-foreground">
              {t("planner.importHint", {
                defaultValue: "Import manuale o tramite Google Classroom (OAuth ufficiale)",
              })}
            </span>
            <ClassroomImport onDone={() => qc.invalidateQueries({ queryKey: ["calendar_events"] })} />
          </>
        }

      />

      <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
      <Card className="card-soft overflow-hidden border-0 p-3">
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          locale={dateLocaleFor(i18n.language)}
          components={{ DayButton: ColoredDayButton }}
          className="mx-auto w-full max-w-[30rem] rounded-md p-2 [--cell-size:clamp(2rem,2.4em,3rem)]"
          classNames={{
            today: "",
            root: "w-full",
            month_grid: "w-full table-fixed",
            month: "flex w-full flex-col gap-3",
            // fixed row height: cells must never grow square with the card width
            day: "group/day relative h-(--cell-size) w-full min-w-0 select-none p-0 text-center [&>button]:min-w-0 [&>button]:w-full [&>button]:px-0",
            week: "mt-1 flex w-full",
            weekday: "text-muted-foreground flex-1 min-w-0 select-none truncate rounded-md text-[0.7em] font-normal",
          }}
        />
        {/* Legenda tipi di evento */}
        <ul
          className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1.5 px-1 pb-1 text-xs text-muted-foreground"
          aria-label={t("planner.legend", { defaultValue: "Legenda tipi di evento" })}
        >
          {(["verifica", "compito", "orale", "hobby"] as const).map((k) => (
            <li key={k} className="inline-flex min-w-0 items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: EVENT_TYPES[k].color }}
                aria-hidden
              />
              <span className="min-w-0 break-words">{EVENT_TYPES[k].label}</span>
            </li>
          ))}
        </ul>
      </Card>


      <Card className="card-soft overflow-hidden border-0 p-5">
        <div className="mb-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
          <h2 className="flex min-w-0 items-center gap-2 text-sm font-medium text-muted-foreground">
            <CalendarDays className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 break-words">
            {isSameDay(selected, new Date())
              ? t("planner.today")
              : selected.toLocaleDateString(i18n.language, { weekday: "long", day: "numeric", month: "long" })}
            </span>
          </h2>
          <AddEventDialog
            defaultDate={selected}
            onDone={() => qc.invalidateQueries({ queryKey: ["calendar_events"] })}
            trigger={
              <Button
                size="icon"
                variant="ghost"
                className="h-10 w-10 shrink-0 rounded-full"
                aria-label={t("planner.addEvent")}
              >
                <Plus className="h-4 w-4" aria-hidden />
              </Button>
            }
          />
        </div>

        {eventsQ.isPending ? (
          <LoadingState />
        ) : eventsQ.isError ? (
          <ErrorState error={eventsQ.error} onRetry={() => eventsQ.refetch()} />
        ) : dayEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("planner.noEventsToday")}</p>
        ) : (
          <div className="space-y-2">
            {dayEvents.map((e) => (
              <EventRow
                key={e.id}
                e={e}
                onChanged={() => qc.invalidateQueries({ queryKey: ["calendar_events"] })}
              />
            ))}
          </div>
        )}
      </Card>
      </div>

      <Card className="card-soft border-0 p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-primary/10 p-2 text-primary"><Sparkles className="h-5 w-5" /></div>
          <div className="text-sm">
            <p className="font-semibold">{t("planner.autoPlanning.title")}</p>
            <p className="text-foreground/70">{t("planner.autoPlanning.description")}</p>
          </div>
        </div>
      </Card>

      <PlanTasksSection selected={selected} />
      <HomeworkSection selected={selected} />
      <ClassScheduleSection />
    </div>
  );
}

/**
 * Fabbrica il componente DayButton per react-day-picker: cerchio colorato
 * sul numero in base al primo evento del giorno, e puntini colorati sotto
 * per gli eventi aggiuntivi (max 4 puntini).
 */
function makeColoredDayButton(eventsByDay: Map<string, CalEvent[]>) {
  return function ColoredDayButton({
    day,
    modifiers,
    className,
    ...props
  }: React.ComponentProps<typeof DayButton>) {
    const dayEvts = eventsByDay.get(dayKey(day.date)) ?? [];
    const primary = dayEvts[0];
    const primaryColor = primary ? colorFor(primary.event_type) : undefined;
    const dots = dayEvts.slice(1, 5);
    const isSelected =
      modifiers.selected &&
      !modifiers.range_start &&
      !modifiers.range_end &&
      !modifiers.range_middle;

    const fullDate = day.date.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const label =
      dayEvts.length > 0
        ? `${fullDate} — ${dayEvts.length}: ${dayEvts.map((ev) => ev.title).join(", ")}`
        : fullDate;

    return (
      <button
        type="button"
        data-day={day.date.toLocaleDateString()}
        data-selected-single={isSelected || undefined}
        aria-label={label}
        aria-current={modifiers.today ? "date" : undefined}
        className={cn(
          "group flex h-(--cell-size) w-full min-w-0 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-md p-0 text-[0.9em] font-normal leading-none",
          "hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
          "data-[selected-single=true]:ring-2 data-[selected-single=true]:ring-primary",
          className,
        )}
        {...props}
      >
        <span
          className={cn(
            "flex h-[1.75em] min-w-[1.75em] items-center justify-center rounded-full px-0.5 transition-colors",
            !primaryColor && "text-foreground",
          )}
          style={
            primaryColor
              ? { backgroundColor: primaryColor, color: textOn(primaryColor) }
              : undefined
          }
        >
          {day.date.getDate()}
        </span>
        <span className="flex h-1.5 items-center gap-0.5" aria-hidden>
          {dots.map((e, i) => (
            <span
              key={i}
              className="h-1 w-1 rounded-full"
              style={{ backgroundColor: colorFor(e.event_type) }}
            />
          ))}
        </span>
      </button>
    );

  };
}

function EventRow({ e, onChanged }: { e: CalEvent; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const EVENT_TYPES = useMemo(() => getEventTypes(t), [t]);
  const tone = EVENT_TYPES[e.event_type]?.tone ?? "sky";
  const color = colorFor(e.event_type);
  const time = e.all_day
    ? t("planner.allDayShort")
    : new Date(e.starts_at).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={[
          time,
          e.title,
          e.subject_name,
          EVENT_TYPES[e.event_type]?.label,
          e.source !== "manual" ? t("planner.edit.titleReadOnly") : t("planner.edit.title"),
        ]
          .filter(Boolean)
          .join(" · ")}
        className={`pastel-${tone} flex w-full min-h-11 flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-border/30 p-3 text-left transition hover:brightness-[0.98]`}
      >
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden
        />
        <span className="min-w-[3.5em] shrink-0 whitespace-nowrap text-xs font-semibold tabular-nums text-foreground/70">
          {time}
        </span>
        <div className="min-w-[8rem] flex-1 basis-40">
          <div className="break-words text-sm font-medium">{e.title}</div>
          {e.subject_name && (
            <div className="break-words text-xs text-foreground/60">{e.subject_name}</div>
          )}
        </div>
        {e.source !== "manual" ? (
          <Badge variant="secondary" className="max-w-full shrink-0 gap-1 whitespace-normal break-words text-xs">
            <Lock className="h-3 w-3 shrink-0" aria-hidden /> {e.source}
          </Badge>
        ) : (
          <Pencil className="h-3.5 w-3.5 shrink-0 text-foreground/40" aria-hidden />
        )}
      </button>

      <EditEventDialog
        open={open}
        onOpenChange={setOpen}
        event={e}
        onChanged={onChanged}
      />
    </>
  );
}

/* ============================================================ */
/*  EDIT EVENT DIALOG                                           */
/* ============================================================ */

function EditEventDialog({
  open,
  onOpenChange,
  event,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  event: CalEvent;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const EVENT_TYPES = useMemo(() => getEventTypes(t), [t]);
  const isManual = event.source === "manual";
  const startDate = new Date(event.starts_at);
  const [title, setTitle] = useState(event.title);
  const [type, setType] = useState<string>(event.event_type);
  const [subject, setSubject] = useState(event.subject_name ?? "");
  const [dateStr, setDateStr] = useState(toLocalDateInput(startDate));
  const [timeStr, setTimeStr] = useState(
    `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}`,
  );
  const [allDay, setAllDay] = useState(event.all_day);
  const [notes, setNotes] = useState(event.notes ?? "");
  const qc = useQueryClient();

  // Re-sync when a different event is opened
  React.useEffect(() => {
    if (!open) return;
    const d = new Date(event.starts_at);
    setTitle(event.title);
    setType(event.event_type);
    setSubject(event.subject_name ?? "");
    setDateStr(toLocalDateInput(d));
    setTimeStr(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
    setAllDay(event.all_day);
    setNotes(event.notes ?? "");
  }, [open, event]);

  const save = useMutation({
    mutationFn: async () => {
      const starts = allDay
        ? new Date(`${dateStr}T00:00:00`)
        : new Date(`${dateStr}T${timeStr}:00`);
      const { error } = await supabase
        .from("calendar_events")
        .update({
          title: title.trim(),
          notes: notes.trim() || null,
          event_type: type,
          subject_name: type === "hobby" ? null : (subject.trim() || null),
          starts_at: starts.toISOString(),
          all_day: allDay,
        })
        .eq("id", event.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      toast.success(t("planner.toast.updated"));
      qc.invalidateQueries({ queryKey: ["calendar_events"] });
      onOpenChange(false);
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("calendar_events").delete().eq("id", event.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      toast.success(t("planner.toast.deleted"));
      qc.invalidateQueries({ queryKey: ["calendar_events"] });
      onOpenChange(false);
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const preview = colorFor(type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{isManual ? t("planner.edit.title") : t("planner.edit.titleReadOnly")}</DialogTitle>
          <DialogDescription>
            {isManual
              ? t("planner.edit.description")
              : t("planner.edit.descriptionImported", { source: event.source })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ev-e-title">{t("planner.fields.title")}</Label>
            <Input id="ev-e-title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={!isManual} />
          </div>

          <div className="space-y-1.5">
            <Label>{t("planner.fields.type")}</Label>
            <Select value={type} onValueChange={setType} disabled={!isManual}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(["verifica", "compito", "orale", "hobby", "event"] as const).map((k) => (
                  <SelectItem key={k} value={k}>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: EVENT_TYPES[k].color }} />
                      {EVENT_TYPES[k].label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
              <span>{t("planner.circleColor")}</span>
              <span
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium"
                style={{ backgroundColor: preview, color: textOn(preview) }}
              >
                {new Date(dateStr).getDate() || "•"}
              </span>
            </div>
          </div>

          {type !== "hobby" && (
            <div className="space-y-1.5">
              <Label htmlFor="ev-e-subject">{t("planner.fields.subject")}</Label>
              <Input id="ev-e-subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!isManual} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="ev-e-date">{t("planner.fields.date")}</Label>
              <Input id="ev-e-date" type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} disabled={!isManual} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ev-e-time">{t("planner.fields.time")}</Label>
              <Input id="ev-e-time" type="time" value={timeStr} onChange={(e) => setTimeStr(e.target.value)} disabled={!isManual || allDay} />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
            <Label htmlFor="ev-e-allday" className="text-sm">{t("planner.fields.allDay")}</Label>
            <Switch id="ev-e-allday" checked={allDay} onCheckedChange={setAllDay} disabled={!isManual} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ev-e-notes">{t("planner.fields.notes")}</Label>
            <Textarea id="ev-e-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} disabled={!isManual} />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => del.mutate()}
            disabled={del.isPending}
          >
            {del.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
            {t("planner.actions.delete")}
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("planner.actions.close")}</Button>
            {isManual && (
              <Button
                onClick={() => save.mutate()}
                disabled={save.isPending || title.trim().length < 2 || !dateStr}
                className="rounded-xl"
              >
                {save.isPending ? (
                  <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("planner.actions.saving")}</>
                ) : (
                  t("planner.actions.save")
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================ */
/*  ADD EVENT DIALOG                                            */
/* ============================================================ */

function toLocalDateInput(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function AddEventDialog({
  defaultDate,
  onDone,
  trigger,
}: {
  defaultDate: Date;
  onDone: () => void;
  trigger?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const EVENT_TYPES = useMemo(() => getEventTypes(t), [t]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<string>("verifica");
  const [subject, setSubject] = useState("");
  const [dateStr, setDateStr] = useState(toLocalDateInput(defaultDate));
  const [timeStr, setTimeStr] = useState("08:00");
  const [allDay, setAllDay] = useState(false);
  const [notes, setNotes] = useState("");
  const qc = useQueryClient();

  // Refresh default date when reopened
  function openChange(next: boolean) {
    if (next) {
      setDateStr(toLocalDateInput(defaultDate));
    }
    setOpen(next);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) throw new Error(t("planner.errors.mustBeLoggedIn"));
      const starts = allDay
        ? new Date(`${dateStr}T00:00:00`)
        : new Date(`${dateStr}T${timeStr}:00`);
      const { error } = await supabase.from("calendar_events").insert({
        user_id: uid,
        title: title.trim(),
        notes: notes.trim() || null,
        event_type: type,
        source: "manual",
        subject_name: type === "hobby" ? null : (subject.trim() || null),
        starts_at: starts.toISOString(),
        all_day: allDay,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      toast.success(t("planner.toast.added"));
      setTitle("");
      setSubject("");
      setNotes("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["calendar_events"] });
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const preview = colorFor(type);

  return (
    <Dialog open={open} onOpenChange={openChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" className="gap-1.5 rounded-xl" aria-label={t("planner.addEvent")}>
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("planner.add.title")}</DialogTitle>
          <DialogDescription>
            {t("planner.add.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ev-title">{t("planner.fields.title")}</Label>
            <Input
              id="ev-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("planner.placeholders.title")}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t("planner.fields.type")}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(["verifica", "compito", "orale", "hobby", "event"] as const).map((k) => (
                  <SelectItem key={k} value={k}>
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: EVENT_TYPES[k].color }}
                      />
                      {EVENT_TYPES[k].label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
              <span>{t("planner.circlePreview")}</span>
              <span
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium"
                style={{ backgroundColor: preview, color: textOn(preview) }}
              >
                {new Date(dateStr).getDate() || "•"}
              </span>
            </div>
          </div>

          {type !== "hobby" && (
            <div className="space-y-1.5">
              <Label htmlFor="ev-subject">{t("planner.fields.subjectOptional")}</Label>
              <Input
                id="ev-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("planner.placeholders.subject")}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="ev-date">{t("planner.fields.date")}</Label>
              <Input
                id="ev-date"
                type="date"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ev-time">{t("planner.fields.time")}</Label>
              <Input
                id="ev-time"
                type="time"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
                disabled={allDay}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
            <Label htmlFor="ev-allday" className="text-sm">{t("planner.fields.allDay")}</Label>
            <Switch id="ev-allday" checked={allDay} onCheckedChange={setAllDay} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ev-notes">{t("planner.fields.notesOptional")}</Label>
            <Textarea
              id="ev-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("planner.actions.cancel")}</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || title.trim().length < 2 || !dateStr}
            className="rounded-xl"
          >
            {mutation.isPending ? (
              <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("planner.actions.saving")}</>
            ) : (
              t("planner.actions.add")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

